import { isOwnerRole } from '../utils/roles';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { Close, CloudUpload, Delete } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { stopWheelNudge } from '../utils/numberInput';
import {
  barePhone,
  formatPhoneInput,
  validatePhoneNumber as validatePhoneField,
} from '../utils/phoneValidation';
import { useAuth } from '../contexts/AuthContext';
import { useMyLocations } from '../hooks/useMyLocations';
import { actingStudioOf } from '../utils/studioOf';

interface InstructorFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  instructor?: any; // If provided, it's edit mode
  studioId: string; // Required for create
}

const InstructorFormDialog: React.FC<InstructorFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  instructor,
  studioId,
}) => {
  // ── ONE PERSON, SEVERAL BRANCHES ────────────────────────────────────────
  //
  // `Instructor.studios` is already an array and the backend already accepts
  // and validates a `studios[]` on create (it must stay inside the brand).
  // Nothing in the panel ever sent one, so an instructor who covers two
  // branches had to be created twice — which then collides with the
  // brand-unique phone index.
  //
  // Multi-select, not duplication: this is the case the array exists for.
  const { isAllLocations, locations, roleByStudio, selectedLocationId } = useMyLocations();
  const [branches, setBranches] = useState<string[]>([]);
  // CREATE: the brand's locations come from the server, so a super admin
  // (who has no location list of their own) gets them too. `editable` says
  // where this user may add an instructor — every location for a super admin.
  type CreateLoc = { _id: string; name: string; isActive: boolean; editable: boolean };
  const [createOptions, setCreateOptions] = useState<CreateLoc[]>([]);
  const [createLocsLoading, setCreateLocsLoading] = useState(false);
  useEffect(() => {
    if (!open || instructor) return;
    // The location being viewed (or the studio page's own studio) anchors the brand.
    const anchor = selectedLocationId || studioId;
    if (!anchor) { setCreateOptions([]); return; }
    let live = true;
    setCreateLocsLoading(true);
    apiService
      .getBrandLocationsForStudio(anchor)
      .then((res: any) => {
        if (!live) return;
        const locs: CreateLoc[] = ((res?.data ?? res)?.locations ?? []).map((l: any) => ({ ...l, _id: String(l._id) }));
        setCreateOptions(locs);
        // Pre-tick where the form was opened from, when the user may add there.
        const here = locs.find((l) => l._id === String(anchor) && l.editable);
        setBranches(here ? [here._id] : []);
      })
      .catch(() => { if (live) { setCreateOptions([]); setBranches([]); } })
      .finally(() => { if (live) setCreateLocsLoading(false); });
    return () => { live = false; };
  }, [open, instructor, selectedLocationId, studioId]);
  const pickLocationsOnCreate = !instructor && createOptions.length > 1;
  const toggleBranch = (id: string) =>
    setBranches((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const isEditMode = Boolean(instructor);
  const { admin } = useAuth();

  // ── Teaches at (edit) ──────────────────────────────────────────────────
  // Read from the server rather than the caller's own switcher list, so a
  // super admin and an owner see the same brand locations; ones the caller
  // can't change are shown but locked.
  type TeachLoc = { _id: string; name: string; isActive: boolean; editable: boolean };
  const [teachLocs, setTeachLocs] = useState<TeachLoc[]>([]);
  const [teachAt, setTeachAt] = useState<string[]>([]);
  const [teachAtInitial, setTeachAtInitial] = useState<string[]>([]);
  const [teachLoading, setTeachLoading] = useState(false);
  const [teachHasLogin, setTeachHasLogin] = useState(false);
  useEffect(() => {
    if (!open || !instructor?._id) {
      setTeachLocs([]); setTeachAt([]); setTeachAtInitial([]);
      return;
    }
    let live = true;
    setTeachLoading(true);
    apiService
      .getInstructorLocations(instructor._id)
      .then((res: any) => {
        if (!live) return;
        const r = res?.data ?? res;
        setTeachLocs((r?.locations ?? []).map((l: any) => ({ ...l, _id: String(l._id) })));
        const cur = (r?.studios ?? []).map(String);
        setTeachAt(cur);
        setTeachAtInitial(cur);
        setTeachHasLogin(!!r?.hasLogin);
      })
      .catch(() => { if (live) setTeachLocs([]); })
      .finally(() => { if (live) setTeachLoading(false); });
    return () => { live = false; };
  }, [open, instructor?._id]);
  const teachChanged =
    teachAt.length !== teachAtInitial.length || teachAt.some((id) => !teachAtInitial.includes(id));
  const toggleTeach = (id: string) =>
    setTeachAt((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  // Studio owners (StudioAdminOnlyGuard) and super-admins (AdminGuard) can grant
  // admin-panel access / reset passwords. Managers cannot (backend rejects them).
  // Owners of any kind (the studio login, studio_owner, brand_owner) and
  // platform admins manage instructor logins.
  const canGrantAccess =
    isOwnerRole(admin?.role) || admin?.role === 'super_admin' || admin?.role === 'admin';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [imageUrlInput, setImageUrlInput] = useState('');

  // Optional "grant admin panel access" (creates a StudioUser login linked to
  // this instructor by phone — same mechanism as the Staff page).
  const [grantAccess, setGrantAccess] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  // Optional studio-page profile, now a collapsible toggle. When on, the instructor
  // is listed (with role/bio/specialties/photo) on the public studio page and inside
  // whitelabelled apps; when off they are not listed there (isPublic = false).
  const [showStudioProfile, setShowStudioProfile] = useState(false);
  /**
   * May this instructor create and edit SESSIONS in the partner app?
   *
   * Defaults ON, matching the backend default: a studio restricts a specific
   * instructor by turning it off, rather than granting it to each one.
   */
  // Off by default (2026-10-09): a studio turns it ON per instructor.
  const [canManageSessions, setCanManageSessions] = useState(false);
  // When editing an instructor that already has a login, this holds it so we can
  // show the email and let the owner/admin reset the password.
  const [linkedLogin, setLinkedLogin] = useState<{ id: string; email: string } | null>(null);
  /**
   * The studio's current instructors, used only to catch a duplicate phone
   * before submitting. Fetched here rather than passed in, so all four call
   * sites (Instructors page, Studio detail, Staff page) get the check without
   * threading a prop through each one. An empty list simply means the check
   * cannot help and the server's answer stands.
   */
  const [existingInstructors, setExistingInstructors] = useState<any[]>([]);
  const [newPassword, setNewPassword] = useState('');
  // Remembers an instructor already created in a partially-failed submit, so a
  // retry updates it instead of creating a duplicate (phoneNumber is unique).
  const createdInstructorId = useRef<string | null>(null);

  const fetchLinkedLogin = useCallback(async (phone: string, name?: string) => {
    if (!canGrantAccess || (!phone && !name)) { setLinkedLogin(null); return; }
    try {
      const res: any = await apiService.getStudioUsers(studioId);
      const list: any[] = Array.isArray(res) ? res : (res?.data || res?.studioUsers || []);
      const real = list.filter((u) => u?.email && !u?._fromInstructorCollection);
      const norm = (v?: string) => (v || '').replace(/\D/g, '').slice(-10);
      const match =
        (phone ? real.find((u) => u?.phone && norm(u.phone) === norm(phone)) : undefined) ??
        (name
          ? real.find(
              (u) =>
                !u?.phone &&
                (u?.name || '').trim().toLowerCase() === name.trim().toLowerCase(),
            )
          : undefined);
      setLinkedLogin(match ? { id: match._id, email: match.email } : null);
    } catch {
      setLinkedLogin(null);
    }
  }, [canGrantAccess, studioId]);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    gender: '',
    image: '',
    description: '',
    role: '',
    bio: '',
    specialties: '', // comma-separated in the form, split to string[] on submit
    displayOrder: 0,
    isPublic: true,
  });

  const populateForm = useCallback(() => {
    if (!instructor) return;

    const imageUrl = instructor.image || '';
    setFormData({
      name: instructor.name || '',
      phoneNumber: barePhone(instructor.phoneNumber || ''),
      gender: instructor.gender || '',
      image: imageUrl,
      description: instructor.description || '',
      role: instructor.role || '',
      bio: instructor.bio || '',
      specialties: Array.isArray(instructor.specialties) ? instructor.specialties.join(', ') : '',
      displayOrder: typeof instructor.displayOrder === 'number' ? instructor.displayOrder : 0,
      isPublic: instructor.isPublic !== false,
    });
    setImageUrlInput(imageUrl);
    // Open the studio-profile section if this instructor is currently listed
    // (isPublic defaults true, so existing instructors stay listed).
    setShowStudioProfile(instructor.isPublic !== false);
    // Off unless explicitly turned on — the same rule the server applies.
    setCanManageSessions((instructor as any).canManageSessions === true);
    setGrantAccess(false);
    setAdminEmail('');
    setAdminPassword('');
    setNewPassword('');
    setLinkedLogin(null);
    createdInstructorId.current = null;
  }, [instructor]);

  useEffect(() => {
    if (open) {
      if (instructor) {
        populateForm();
        fetchLinkedLogin(instructor.phoneNumber || '', instructor.name);
      } else {
        resetForm();
        // Only needed when creating — an edit keeps its own number.
        apiService
          .getInstructors(studioId)
          .then((res: any) =>
            setExistingInstructors(res?.instructors || res?.data || []),
          )
          .catch(() => setExistingInstructors([]));
      }
    }
  }, [open, instructor, studioId, populateForm, fetchLinkedLogin]);

  const resetForm = () => {
    setFormData({
      name: '',
      phoneNumber: '',
      gender: '',
      image: '',
      description: '',
      role: '',
      bio: '',
      specialties: '',
      displayOrder: 0,
      isPublic: true,
    });
    setImageUrlInput('');
    setShowStudioProfile(false);
    setCanManageSessions(false);
    setGrantAccess(false);
    setAdminEmail('');
    setAdminPassword('');
    setNewPassword('');
    setLinkedLogin(null);
    createdInstructorId.current = null;
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(`File "${file.name}" is not an image file`);
      event.target.value = '';
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(`Image "${file.name}" size should be less than 5MB`);
      event.target.value = '';
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);

      // Upload file to S3 (admin endpoint supports multiple files, but we'll send one)
      const response = await apiService.uploadFiles([file], 'instructors');
      const imageUrls = response.fileLinks || [];

      if (imageUrls.length > 0) {
        // Set the first uploaded image URL
        setFormData(prev => ({
          ...prev,
          image: imageUrls[0],
        }));
        setImageUrlInput(imageUrls[0]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      image: '',
    }));
    setImageUrlInput('');
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate the admin-access fields up front (before saving anything) so a
      // bad email/password can't leave us with a saved instructor + no access.
      // Grant a NEW login (no existing login) vs reset an EXISTING login's password.
      const wantsAccess = canGrantAccess && !linkedLogin && grantAccess;
      const wantsReset = canGrantAccess && !!linkedLogin && newPassword.trim().length > 0;
      const loginEmail = adminEmail.trim().toLowerCase();
      if (wantsAccess) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
          setError('Enter a valid login email to grant admin access');
          setLoading(false);
          return;
        }
        if (adminPassword.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }
      }
      if (wantsReset && newPassword.trim().length < 6) {
        setError('New password must be at least 6 characters');
        setLoading(false);
        return;
      }

      const nextPhoneError = validatePhoneField(formData.phoneNumber);
      setPhoneError(nextPhoneError);
      if (nextPhoneError) {
        setLoading(false);
        return;
      }

      // ── Catch a duplicate phone BEFORE the request ────────────────────
      // `Instructor.phoneNumber` is unique, so a repeat detonates an E11000
      // inside Mongo. Nothing catches it, and with no exception filter on the
      // API that surfaces as a bare 500 whose body literally reads "Internal
      // server error" — which is exactly what the panel then displayed. Asking
      // the list we already hold turns the common case into an instant, named
      // answer with no request at all.
      if (!isEditMode && !createdInstructorId.current) {
        const digits = (v: string) => (v || '').replace(/\D/g, '').slice(-10);
        const clash = existingInstructors.find(
          (i) => digits(i.phoneNumber) === digits(formData.phoneNumber),
        );
        if (clash) {
          setPhoneError(`Already used by ${clash.name} — enter a different number.`);
          setLoading(false);
          return;
        }
      }

      // Payload includes all fields - optional fields can be empty strings
      const payload = {
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        gender: formData.gender,
        image: formData.image || '', // Optional - can be empty
        description: formData.description || '', // Optional - can be empty
        // Studio-page (richer) fields — all optional.
        role: formData.role || '',
        bio: formData.bio || '',
        specialties: formData.specialties
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        displayOrder: Number.isFinite(formData.displayOrder) ? formData.displayOrder : 0,
        // The studio-profile toggle is the single "list on studio page" control.
        isPublic: showStudioProfile,
        canManageSessions,
      };

      // Every write below is pinned to one location — under "All locations"
      // the switcher sends __all__, which the server refuses for writes.
      const grantAt = isEditMode
        ? actingStudioOf(instructor, {
            isAllLocations,
            myLocationIds: locations.map((l) => String(l._id)),
            roleByStudio,
          })
        : (pickLocationsOnCreate ? branches[0] : null) || studioId;
      if (isEditMode) {
        if (teachLocs.length > 1 && teachAt.length === 0) {
          setError('Choose at least one location this instructor teaches at.');
          setLoading(false);
          return;
        }
        await apiService.updateInstructor(
          instructor._id,
          payload,
          grantAt,
        );
        if (teachChanged) {
          try {
            await apiService.setInstructorLocations(instructor._id, teachAt, grantAt);
            setTeachAtInitial(teachAt);
          } catch (e: any) {
            setError(
              'Details saved, but locations were not changed: ' +
                (e?.response?.data?.message || e?.message || 'unknown error'),
            );
            setLoading(false);
            return;
          }
        }
      } else if (createdInstructorId.current) {
        // A previous submit already created the instructor (the access grant
        // failed); update it instead of creating a duplicate (phone is unique).
        await apiService.updateInstructor(
          createdInstructorId.current,
          payload,
          grantAt,
        );
      } else {
        if (pickLocationsOnCreate && branches.length === 0) {
          setError('Tick at least one location this instructor teaches at.');
          setLoading(false);
          return;
        }
        // The first chosen branch is the instructor's home studio; the rest
        // ride along in `studios[]`, which the backend validates against the
        // brand.
        const homeStudio = (pickLocationsOnCreate ? branches[0] : studioId) || studioId;
        if (!homeStudio) {
          setError('Studio ID is required');
          setLoading(false);
          return;
        }
        const res: any = await apiService.createInstructor(homeStudio, {
          ...payload,
          ...(pickLocationsOnCreate && branches.length ? { studios: branches } : {}),
        });
        createdInstructorId.current = res?.instructor?._id || res?._id || null;
      }

      // Optionally grant admin-panel login for this instructor. The backend
      // links it to the instructor by phone (same as the Staff page). Done after
      // the instructor exists; if it fails, the instructor is already saved.
      if (wantsAccess) {
        try {
          await apiService.createStudioUser({
            name: formData.name,
            email: loginEmail,
            password: adminPassword,
            phone: formData.phoneNumber,
            role: 'instructor',
            // The first ticked location on create (it may not be the page's own).
            studioId: (!isEditMode && pickLocationsOnCreate && branches[0]) || studioId,
          }, grantAt);
        } catch (e: any) {
          setError(
            'Instructor saved, but admin access was NOT granted: ' +
              (e?.response?.data?.message || e?.message || 'unknown error') +
              '. Fix the email/password and submit again.',
          );
          setLoading(false);
          return;
        }
      }

      // Created at several locations with a login: give that login access at
      // every one of them (the login itself is granted at the first only).
      if (!isEditMode && wantsAccess && pickLocationsOnCreate && branches.length > 1 && createdInstructorId.current) {
        try {
          await apiService.setInstructorLocations(createdInstructorId.current, branches, branches[0]);
        } catch (e: any) {
          setError(
            'Instructor and login saved, but access at the other locations was not set: ' +
              (e?.response?.data?.message || e?.message || 'unknown error') +
              '. Open the instructor and save the locations again.',
          );
          setLoading(false);
          return;
        }
      }

      // Reset the password of an existing linked login.
      if (wantsReset && linkedLogin) {
        try {
          await apiService.updateStudioUser(linkedLogin.id, { password: newPassword.trim() }, grantAt);
        } catch (e: any) {
          setError(
            'Instructor saved, but the password reset failed: ' +
              (e?.response?.data?.message || e?.message || 'unknown error'),
          );
          setLoading(false);
          return;
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      
      const status = err?.status ?? err?.response?.status;
      const apiMessage = err?.message ?? err?.response?.data?.message;
      // A 500 carrying Nest's default text tells the operator nothing. The only
      // unique constraint on an instructor is the phone number, and it is
      // unique ACROSS STUDIOS — so the clashing instructor can sit at a studio
      // this account cannot see, which is why the pre-check above cannot catch
      // every case. Name the likely cause instead of relaying "Internal server
      // error", which reads as "the app is broken" rather than "change this
      // field".
      const isOpaque500 =
        status === 500 && (!apiMessage || /internal server error/i.test(apiMessage));
      if (isOpaque500) {
        // `phoneNumber` is the ONLY unique index on the instructors collection,
        // so a save that dies this way is a duplicate number — not a maybe.
        // Stated plainly, and put on the field that has to change rather than
        // in a banner the operator has to interpret.
        setPhoneError('Already used by another instructor — enter a different number.');
        // No second banner: one clear message, attached to the input at fault.
        setError(null);
      } else {
        setError(apiMessage || err.message || 'Operation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditMode ? 'Edit Instructor' : 'Create Instructor'}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box display="flex" flexDirection="column" gap={2}>
          {/* ── Locations ── one person, one record, several locations.
              Edit reads the brand's locations from the server; create offers
              the locations where the user is manager or above. */}
          {((isEditMode && (teachLoading || teachLocs.length > 1)) || (!isEditMode && (createLocsLoading || pickLocationsOnCreate))) && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
              <Typography fontWeight={600} fontSize={14}>Locations</Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Tick every location this instructor teaches at.
              </Typography>
              {(isEditMode ? teachLoading : createLocsLoading) ? (
                <Box display="flex" alignItems="center" gap={1} py={0.5}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">Loading locations…</Typography>
                </Box>
              ) : (
                <>
                  <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
                    {(isEditMode ? teachLocs : createOptions)
                      .map((l) => ({ id: l._id, label: l.isActive ? l.name : `${l.name} (inactive)`, editable: l.editable }))
                      .map((o) => {
                      const checked = isEditMode ? teachAt.includes(o.id) : branches.includes(o.id);
                      const control = (
                        <FormControlLabel
                          key={o.id}
                          control={
                            <Checkbox
                              id={`instructor-location-${o.id}`}
                              size="small"
                              checked={checked}
                              disabled={!o.editable || loading}
                              onChange={() => (isEditMode ? toggleTeach(o.id) : toggleBranch(o.id))}
                            />
                          }
                          label={o.label}
                        />
                      );
                      return o.editable ? control : (
                        <Tooltip key={o.id} title="You can change this only where you are a manager or owner" placement="top-start">
                          <span>{control}</span>
                        </Tooltip>
                      );
                    })}
                  </FormGroup>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {isEditMode && teachHasLogin
                      ? 'Their login follows: they can switch to each ticked location and see their own sessions and bookings there. '
                      : 'Their login, if you create one, gets access at each ticked location. '}
                    {isEditMode && 'A location can be unticked only after their classes and upcoming sessions there are handed over.'}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Name */}
          <TextField
            label="Name *"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            size="small"
          />

          {/* Phone Number */}
          <TextField
            label="Phone Number *"
            placeholder="9876543210"
            value={formData.phoneNumber}
            onChange={(e) => {
              setPhoneError(undefined);
              handleChange('phoneNumber', formatPhoneInput(e.target.value));
            }}
            fullWidth
            size="small"
            error={Boolean(phoneError)}
            helperText={phoneError}
            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
            InputProps={{
              startAdornment: (
                <Typography variant="body2" sx={{ mr: 0.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                  +91
                </Typography>
              ),
            }}
          />

          {/* Gender */}
          <FormControl fullWidth size="small">
            <InputLabel>Gender *</InputLabel>
            <Select
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
              label="Gender *"
            >
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Image Upload */}
          <Box>
            <Typography variant="body2" fontWeight={600} mb={1}>
              Image
            </Typography>
            {formData.image ? (
              <Box position="relative" display="inline-block">
                <Box
                  component="img"
                  src={formData.image}
                  alt="Instructor"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 200,
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={handleRemoveImage}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.7)',
                    },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                disabled={uploadingImage}
                fullWidth
                sx={{
                  py: 2,
                  borderStyle: 'dashed',
                }}
              >
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileSelect}
                />
              </Button>
            )}
            {/* Manual URL Input (optional) */}
            {!formData.image && (
              <Box mt={1}>
                <TextField
                  label="Or enter Image URL"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  onBlur={() => {
                    if (imageUrlInput.trim()) {
                      handleChange('image', imageUrlInput.trim());
                    }
                  }}
                  fullWidth
                  size="small"
                  helperText="You can upload an image or paste an image URL"
                />
              </Box>
            )}
          </Box>

          {/* Description */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={3}
            fullWidth
            size="small"
          />

          {/* ── Admin-panel access (studio owners + super-admins) ───────── */}
          {canGrantAccess && (
            <>
              <Divider sx={{ mt: 1 }} />
              {isEditMode && linkedLogin ? (
                /* Instructor already has a login → show it + allow password reset */
                <>
                  <Typography variant="body2" fontWeight={600}>
                    Admin panel access:{' '}
                    <Box component="span" sx={{ color: 'success.main' }}>{linkedLogin.email}</Box>
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                    Passwords are encrypted and cannot be shown. To change it, enter
                    a new password below (leave blank to keep the current one).
                  </Typography>
                  <TextField
                    label="Reset Password (min 6 characters)"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                    size="small"
                    autoComplete="new-password"
                    placeholder="Leave blank to keep current password"
                  />
                </>
              ) : (
                /* No login yet → offer to grant one */
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={grantAccess}
                        onChange={(e) => setGrantAccess(e.target.checked)}
                      />
                    }
                    label="Grant admin panel access (login)"
                  />
                  {grantAccess && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                        Creates a login for this instructor (read-only instructor
                        access). They sign in to the admin panel with this email and
                        password.
                      </Typography>
                      <TextField
                        label="Login Email *"
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="off"
                      />
                      <TextField
                        label="Password * (min 6 characters)"
                        type="password"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="new-password"
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ── Permissions ──────────────────────────────────────────────
              Shown to SUPER ADMINS and STUDIO OWNERS — the same set that can
              grant panel access (`canGrantAccess`). Managers are excluded on
              purpose: who may rewrite the schedule is an owner's decision, and a
              manager could otherwise grant it by proxy. Instructors never open
              this dialog at all. */}
          {canGrantAccess && (
          <>
          <Divider sx={{ mt: 1 }} />
          <FormControlLabel
            control={
              <Switch
                checked={canManageSessions}
                onChange={(e) => setCanManageSessions(e.target.checked)}
              />
            }
            label="Can manage sessions"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Off by default — turn it on to let this instructor create and edit
            sessions in the partner app. Bookings, packs, classes and coupons stay
            closed to instructors either way, and this changes nothing for owners
            or managers.
          </Typography>
          </>
          )}

          {/* ── Studio-page profile (optional, toggle — kept last) ───────── */}
          <Divider sx={{ mt: 1 }} />
          <FormControlLabel
            control={
              <Switch
                checked={showStudioProfile}
                onChange={(e) => setShowStudioProfile(e.target.checked)}
              />
            }
            label="Studio page profile (optional)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            When on, this instructor is listed — with their role, bio, specialties and
            photo — on the public studio page and inside whitelabelled apps (e.g. Purple
            Pilates, House of Pilates). Leave off for staff who shouldn't appear publicly.
          </Typography>
          {showStudioProfile && (
            <>
              {/* Role */}
              <TextField
                label="Role / Title"
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                fullWidth
                size="small"
                placeholder="e.g. Senior Pilates Instructor"
              />

              {/* Bio */}
              <TextField
                label="Bio"
                value={formData.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
              />

              {/* Specialties */}
              <TextField
                label="Specialties"
                value={formData.specialties}
                onChange={(e) => handleChange('specialties', e.target.value)}
                fullWidth
                size="small"
                placeholder="Reformer, Prenatal, Rehab"
                helperText="Comma-separated list"
              />

              {/* Display order */}
              <TextField
                label="Display Order"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => handleChange('displayOrder', parseInt(e.target.value, 10) || 0)}
                onWheel={stopWheelNudge}
                fullWidth
                size="small"
                helperText="Lower numbers appear first on the studio page"
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name || !formData.phoneNumber || !formData.gender || uploadingImage}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstructorFormDialog;

